import { Head, router, useForm } from "@inertiajs/react"

import {
  type Business,
  type Neighborhood,
  RaffleForm,
  type RaffleFormData,
  type ViralCampaignFormData,
} from "@/components/raffles/raffle-form"
import AppLayout from "@/layouts/app-layout"
import { rafflePath } from "@/routes"

interface RafflesEditProps {
  raffle: {
    id: number
    title: string
    description: string
    prize_description: string
    prize_value: number | string | null
    start_date: string | null
    end_date: string | null
    city?: { id: number; name: string } | null
    neighborhood?: { id: number; name: string } | null
    sponsor?: { id: number; business_name: string } | null
    max_participants?: number | string | null
    winner_count: number | string
    terms_and_conditions: string
    requirements?: Record<string, string | number>
    image?: string | null
    create_viral_campaign?: boolean
    viral_campaign?: {
      base_entries?: number
      entries_per_referral?: number
      max_entries_per_user?: number
      status?: "draft" | "active" | "ended" | "drawn"
    }
  }
  neighborhoods: Neighborhood[]
  businesses: Business[]
}

export default function RafflesEdit({
  raffle,
  neighborhoods,
  businesses,
}: RafflesEditProps) {
  const toDateTimeLocal = (value: string | null | undefined) => {
    if (!value) return ""
    const d = new Date(value)
    if (isNaN(d.getTime())) return ""
    const pad = (n: number) => n.toString().padStart(2, "0")
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  const initial: RaffleFormData = {
    title: raffle.title ?? "",
    description: raffle.description ?? "",
    prize_description: raffle.prize_description ?? "",
    prize_value: (raffle.prize_value ?? "").toString(),
    start_date: toDateTimeLocal(raffle.start_date),
    end_date: toDateTimeLocal(raffle.end_date),
    city_id: (raffle.city?.id ?? "") as string | number,
    neighborhood_id: (raffle.neighborhood?.id ?? "").toString(),
    max_participants: (raffle.max_participants ?? "").toString(),
    winner_count: (raffle.winner_count ?? "1").toString(),
    sponsor_id: (raffle.sponsor?.id ?? "").toString(),
    terms_and_conditions: raffle.terms_and_conditions ?? "",
    requirements: raffle.requirements ?? {},
    image: null,
    image_url: "",
    remove_image: false,
    create_viral_campaign: Boolean(raffle.create_viral_campaign),
    viral_campaign: {
      base_entries: raffle.viral_campaign?.base_entries ?? 1,
      entries_per_referral: raffle.viral_campaign?.entries_per_referral ?? 5,
      max_entries_per_user: raffle.viral_campaign?.max_entries_per_user ?? 100,
      status: raffle.viral_campaign?.status ?? "active",
    },
  }

  const { data, setData, put, processing, errors } = useForm<{
    raffle: RaffleFormData & { viral_campaign: ViralCampaignFormData }
  }>({
    raffle: initial as RaffleFormData & {
      viral_campaign: ViralCampaignFormData
    },
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    // forceFormData asegura que los nested (viral_campaign) y posibles archivos viajen bien
    put(rafflePath(raffle.id), { forceFormData: true })
  }

  const err = errors as unknown as Record<string, string>

  return (
    <AppLayout>
      <Head title={`Editar ${raffle.title}`} />

      <div className="mx-auto max-w-6xl p-4 md:p-6">
        <RaffleForm
          title="Editar sorteo"
          data={data.raffle}
          errors={err}
          neighborhoods={neighborhoods}
          businesses={businesses}
          processing={processing}
          onChange={(partial) =>
            setData("raffle", { ...data.raffle, ...partial })
          }
          onSubmit={submit}
          onCancel={() => router.visit(rafflePath(raffle.id))}
          submitLabel="Guardar cambios"
          currentImage={raffle.image ?? null}
        />
      </div>
    </AppLayout>
  )
}
